import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useForm } from "react-hook-form";
import produce from "immer";

import "./Models.scss";
import { ErrorBoundary, ErrorMessage, FormInput, PageHeader, Loader, FormSelect, Page } from "components";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, IconButton} from '@cfa/react-components';
import { AsyncStatus } from "enums";
import { IDeviceModel, IDeviceModelAPI } from "interfaces";
import { ManufacturersService, parseAxiosError } from "services";

interface IModelPageState {
    loadStatus: AsyncStatus;
    loadError?: string;
    deleteStatus?: AsyncStatus;
    deleteError?: string;
    createStatus?: AsyncStatus;
    createError?: string;
    models?: IDeviceModel[];
    deleteModel?: IDeviceModel;
}

type IModelForm = {
    name: string;
    manID: number;
};

export function Models() {

    const [pageState, setPageState] = useState<IModelPageState>({
        loadStatus: AsyncStatus.Loading,
        createStatus: AsyncStatus.Initial,
        deleteStatus: AsyncStatus.Initial,
        models: [],
    });
    const { loadStatus, loadError, deleteStatus, deleteError, createStatus, createError, models, deleteModel } = pageState;
    
    const form = useForm<IModelForm>({ mode: "onTouched" });

    // create modal control
    const [createShow, setCreateShow] = useState(false);
    const handleCreateClose = () => setCreateShow(false);
    const showCreateModal = () => {
        setCreateShow(true);
    };

    // delete modal control
    const [deleteShow, setDeleteShow] = useState(false);
    const handleDeleteClose = () => setDeleteShow(false);
    const showDeleteModal = (m) => {
        setPageState(
            produce((draft) => {
                draft.deleteModel = m;
            })
        );
        setDeleteShow(true);
    };

    // data is preloaded in app.tsx, so just assign from service
    useEffect(() => {
        if (loadStatus === AsyncStatus.Success) return;
        
        setPageState(
            produce((draft) => {
                draft.loadStatus = AsyncStatus.Success;
                draft.models = ManufacturersService.models;
            })
        );
    });
 
    // Soft deletes the Model in the DB
    function onDeleteModel(mod: IDeviceModel) {
        if (deleteStatus === AsyncStatus.Loading) return;

        setPageState(
            produce((draft) => {
                draft.deleteModel = mod;
                draft.deleteError = '';
                draft.deleteStatus = AsyncStatus.Loading;
            })
        )

        ManufacturersService.deleteModel(mod.id)
            .then(() => {
                setPageState(
                    produce((draft) => {
                        draft.deleteModel = undefined;
                        draft.deleteStatus = AsyncStatus.Success;
                        draft.models = ManufacturersService.models;
                    })
                )
                setDeleteShow(false);
            })
            .catch((err) => {
                setPageState(
                    produce((draft) => {
                        draft.deleteError = parseAxiosError(err);
                        draft.deleteStatus = AsyncStatus.Error;
                    })
                );
            });
        
    }

    const onCreateModel = () => {
        if (createStatus === AsyncStatus.Loading) return;

        form.handleSubmit((formData: IModelForm) => {
            if (!formData.manID) return;
            
            const modelNames = models.map((m) => String(m.manufacturerId)+m.name);
            if (modelNames.includes(String(formData.manID)+formData.name)) {
                setPageState(produce(draft => {
                    draft.createError = `${formData.name} already exists for that manufacturer.`;
                    draft.createStatus = AsyncStatus.Error;
                }));
                return;
            }

            // Setstate to loading
            setPageState(produce(draft => {
                draft.createError = '';
                draft.createStatus = AsyncStatus.Loading;
            }));

            let newMod: IDeviceModelAPI = {
                name: formData.name,
                manufacturerId: Number(formData.manID),
            }
            
            ManufacturersService.postModel(newMod)
                .then(() => {
                    setPageState(produce(draft => {
                        draft.createError = '';
                        draft.createStatus = AsyncStatus.Success;
                        draft.models = ManufacturersService.models;
                    }));
                    setCreateShow(false);
                })
                .catch((err) => {
                    setPageState(
                        produce((draft) => {
                            draft.createError = parseAxiosError(err);
                            draft.createStatus = AsyncStatus.Error;
                        })
                    );
                });
        })();
    };    

    function onChangeManufacturer(e) {
        form.setValue('name', '');
    }

    return (<>
        <Page>
            <PageHeader title="Models" right={<Button onClick={showCreateModal}>{'New Model'}</Button>} />

            <div className="row models_colHeaders">
                <div className="models_colHeader models_colName">Name</div>
                <div className="models_colHeader models_colManHeader">Manufacturer</div>
            </div>
            
            <div className="models_list">
                <ErrorBoundary>
                    {loadStatus === AsyncStatus.Loading && <Loader />}
                    {loadStatus === AsyncStatus.Success &&
                        models.length > 0 && models.map((mod) => {
                            return (
                                <div className="models_listItem" key={mod.id}>
                                    <div className="models_colName">{mod.name}</div>
                                    <div className="models_colMan">{mod.manufacturerName}</div>
                                    <div>
                                        <IconButton
                                            size="sm"
                                            disabled={mod.isDeleted}
                                            onClick={() => showDeleteModal(mod)}
                                        >
                                            <FontAwesomeIcon icon="trash" />
                                        </IconButton>
                                    </div>
                                </div>
                            );
                        })}
                    {loadStatus === AsyncStatus.Success &&
                        models.length === 0 && (
                            <div className="models_listEmpty">
                                <FontAwesomeIcon
                                    icon="search"
                                    className="models_listEmptyIcon"
                                />
                                <div className="models_listEmptyText">
                                    No models found.
                                </div>
                            </div>
                        )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                </ErrorBoundary>
            </div>
        </Page>

        <Modal show={deleteShow} onClose={handleDeleteClose}>
            <ModalHeader>
                Delete Model
            </ModalHeader>
            <ModalBody>
                <p>Are you sure you want to delete this model?</p>
                <div className="models_deleteHeader">
                    Name: {deleteModel && deleteModel.name}<br/>
                    ID: {deleteModel && deleteModel.id}
                </div>
            </ModalBody>
            <ModalFooter>
                {deleteStatus === AsyncStatus.Error && (
                    <ErrorMessage message={deleteError} />
                )}
                <Button variant="outlined" disabled={deleteStatus === AsyncStatus.Loading} onClick={handleDeleteClose}>
                    Cancel
                </Button>
                <Button loading={deleteStatus === AsyncStatus.Loading} onClick={() => onDeleteModel(deleteModel)}>
                    Delete
                </Button>
            </ModalFooter>
        </Modal>

        <Modal show={createShow} onClose={handleCreateClose}>
            <ModalHeader>
                Create Model
            </ModalHeader>
            <ModalBody>
                <div>
                    {createStatus === AsyncStatus.Error && (
                        <ErrorMessage message={createError} />
                    )}
                    <div className="row">
                        <div className="col">
                            <FormSelect
                                form={form}
                                getOptionLabel={m => m.name}
                                getOptionValue={m => m.id}
                                label="Manufacturer"
                                name="manID"
                                options={ManufacturersService.manufacturers.filter(m => (!m.isDeleted))}
                                onChange={onChangeManufacturer}
                            />
                        </div>
                    </div>
                    <div className="row">
                        <div className="col">
                            <FormInput
                                form={form}
                                label="Model"
                                name="name"
                                registerOptions={{ required: true }}
                            />   
                        </div>
                    </div>      
                </div>
            </ModalBody>
            <ModalFooter>
                <Button variant="outlined" disabled={createStatus === AsyncStatus.Loading} onClick={handleCreateClose}>
                    Cancel
                </Button>
                <Button loading={createStatus === AsyncStatus.Loading} disabled={!form.formState.isValid} onClick={onCreateModel}>
                    Submit
                </Button>
            </ModalFooter>
        </Modal>
    </>);
}
